<script>
  import { t } from '../../services/i18n.js';

  /**
   * @type {{ 
   *   item: import('../../services/inventoryService.js').InventoryItem, 
   *   onclick: () => void 
   * }}
   */
  let { item, onclick } = $props();

  /**
   * Format price in Brazilian Real
   * @param {number} price
   */
  function formatPrice(price) {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onclick();
    }
  }
</script>

<button 
  class="item-card"
  type="button"
  {onclick}
  onkeydown={handleKeyDown}
  aria-label={`${item.name} - ${formatPrice(item.price)}`}
>
  <div class="item-image-container">
    {#if item.imageUrl}
      <img 
        src={item.imageUrl} 
        alt={item.name}
        class="item-image"
        loading="lazy"
        onerror={(e) => e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23f0f0f0" width="100" height="100"/><text x="50" y="50" text-anchor="middle" dy=".3em" fill="%23999" font-size="20">📷</text></svg>'}
      />
    {:else}
      <div class="item-image-placeholder">
        <span aria-hidden="true">📷</span>
      </div>
    {/if}
    
    {#if !item.available}
      <div class="item-badge sold">{$t('item.sold')}</div>
    {:else}
      <div class="item-badge available">{$t('item.available')}</div>
    {/if}
  </div>

  <div class="item-content">
    <h3 class="item-name">
      {#if item.brand && item.brand.trim() !== '' && item.brand.toLowerCase() !== 'não identificada'}
        {item.name} - {item.brand}
      {:else}
        {item.name}
      {/if}
    </h3>
    
    <div class="item-meta">
      {#if item.size}
        <span class="item-size"><strong>TAM:</strong> {item.size}</span>
      {/if}
      <span class="item-category">{item.category}</span>
    </div>

    <div class="item-price">
      {formatPrice(item.price)}
    </div>
  </div>
</button>

<style>
  .item-card {
    background-color: var(--color-surface);
    border-radius: var(--border-radius-lg);
    overflow: hidden;
    box-shadow: var(--shadow-sm);
    transition: all var(--transition-fast);
    cursor: pointer;
    border: 2px solid transparent;
    padding: 0;
    width: 100%;
    text-align: left;
  }

  .item-card:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-md);
    border-color: var(--color-primary-hover);
  }

  .item-card:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px var(--color-primary-alpha);
  }

  .item-image-container {
    position: relative;
    aspect-ratio: 1;
    overflow: hidden;
    background-color: var(--color-background);
  }

  .item-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform var(--transition-normal);
  }

  .item-card:hover .item-image {
    transform: scale(1.05);
  }

  .item-image-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--color-background);
    font-size: 3rem;
  }

  .item-badge {
    position: absolute;
    top: var(--spacing-sm);
    right: var(--spacing-sm);
    padding: var(--spacing-xs) var(--spacing-sm);
    border-radius: var(--border-radius-sm);
    font-size: var(--font-size-xs);
    font-weight: 600;
    text-transform: uppercase;
    box-shadow: var(--shadow-sm);
  }

  .item-badge.available {
    background-color: var(--color-success);
    color: var(--color-brand-black);
  }

  .item-badge.sold {
    background-color: var(--color-error);
    color: white;
  }

  .item-content {
    padding: var(--spacing-md);
  }

  .item-name {
    font-size: var(--font-size-base);
    font-weight: 600;
    color: var(--color-text);
    margin-bottom: var(--spacing-xs);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .item-meta {
    display: flex;
    gap: var(--spacing-sm);
    margin-bottom: var(--spacing-sm);
  }

  .item-size,
  .item-category {
    font-size: var(--font-size-xs);
    color: var(--color-brand-black);
    background-color: var(--color-brand-lavender);
    padding: 4px var(--spacing-sm);
    border-radius: var(--border-radius-sm);
    font-weight: 500;
  }

  .item-size strong,
  .item-category strong {
    font-weight: 700;
    opacity: 0.8;
  }

  .item-price {
    font-size: var(--font-size-lg);
    font-weight: 700;
    color: var(--color-secondary);
  }

  @media (max-width: 480px) {
    .item-content {
      padding: var(--spacing-sm);
    }

    .item-name {
      font-size: var(--font-size-sm);
    }

    .item-price {
      font-size: var(--font-size-base);
    }
  }
</style>
